import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import type { ReactNode } from "react";
import type { CrmContactDetail } from "../../api/contacts";

interface Props {
  open: boolean;
  loading: boolean;
  detail: CrmContactDetail | null;
  onClose: () => void;
}

function formatDateTime(value?: string | null): string {
  if (!value) return "None";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "None";
  return date.toLocaleString();
}

function Section({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <Box>
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

export default function ContactDetailDrawer({
  open,
  loading,
  detail,
  onClose,
}: Props) {
  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: { xs: 360, sm: 460 }, maxWidth: "100vw", p: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="h6">
              {detail?.contact.display_name || "Unknown contact"}
            </Typography>
            {detail?.contact.display_name_source && (
              <Typography variant="body2" color="text.secondary">
                {detail.contact.display_name_source}
              </Typography>
            )}
          </Box>
          <IconButton aria-label="Close contact details" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>

        <Divider sx={{ my: 2 }} />

        {loading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : !detail ? (
          <Typography color="text.secondary">No contact selected.</Typography>
        ) : (
          <Stack spacing={3}>
            <Section title="Identities">
              <Stack direction="row" gap={1} flexWrap="wrap">
                {detail.identities.length === 0 ? (
                  <Typography variant="body2">None</Typography>
                ) : (
                  detail.identities.map((identity) => (
                    <Chip
                      key={identity.crm_identity_id}
                      size="small"
                      variant="outlined"
                      label={`${identity.provider}: ${identity.label || "Unknown"}`}
                    />
                  ))
                )}
              </Stack>
            </Section>

            <Section title="Observations">
              <Stack spacing={1}>
                {detail.observations.length === 0 ? (
                  <Typography variant="body2">None</Typography>
                ) : (
                  detail.observations.map((observation, index) => (
                    <Box key={`${observation.type}-${index}`}>
                      <Typography variant="body2">
                        {observation.value || "Unknown"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {observation.type} / {observation.source_channel || "unknown"} /{" "}
                        {formatDateTime(observation.observed_at)}
                      </Typography>
                    </Box>
                  ))
                )}
              </Stack>
            </Section>

            <Section title="Bookings">
              <Stack spacing={1}>
                {detail.bookings.length === 0 ? (
                  <Typography variant="body2">None</Typography>
                ) : (
                  detail.bookings.map((booking) => (
                    <Box key={booking.booking_id}>
                      <Typography variant="body2">
                        {booking.customer_name_snapshot || "Unknown customer"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {booking.status || "unknown"} / {formatDateTime(booking.start_time)}
                      </Typography>
                    </Box>
                  ))
                )}
              </Stack>
            </Section>

            <Section title="Conversations">
              <Stack spacing={1}>
                {detail.conversations.length === 0 ? (
                  <Typography variant="body2">None</Typography>
                ) : (
                  detail.conversations.map((conversation) => (
                    <Box key={conversation.conversation_id}>
                      <Typography variant="body2">
                        {conversation.primary_display_name || "Unknown"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {conversation.channel || "unknown"} /{" "}
                        {conversation.identity_resolution_status || "unresolved"} /{" "}
                        {formatDateTime(conversation.last_activity_at)}
                      </Typography>
                    </Box>
                  ))
                )}
              </Stack>
            </Section>

            <Section title="Possible Matches">
              <Stack spacing={1}>
                {detail.possible_matches.length === 0 ? (
                  <Typography variant="body2">None</Typography>
                ) : (
                  detail.possible_matches.map((match) => (
                    <Box key={match.crm_possible_match_id}>
                      <Typography variant="body2">{match.reason_code}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {String(match.confidence ?? "unknown")} /{" "}
                        {formatDateTime(match.created_at)}
                      </Typography>
                    </Box>
                  ))
                )}
              </Stack>
            </Section>
          </Stack>
        )}
      </Box>
    </Drawer>
  );
}
